import React, { useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  CButton,
  CCard,
  CCardBody,
  CCol,
  CImage,
  CRow,
  CSpinner,
  CBadge,
  CNav,
  CNavItem,
  CNavLink,
  CTabContent,
  CTabPane,
  CFormTextarea,
} from "@coreui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faAddressCard,
  faBriefcase,
  faListCheck,
  faCalendarDays,
  faFileLines,
  faClose,
  faDownload,
  faEye,
  faCheck,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { Formik } from "formik";
import { toast } from "react-toastify";
import { useTranslationContext } from "@/context/TranslationContext";
import { freelancerUser } from "@/constants/translation/freelancerUser.translation";
import { breadcrumb_key } from "@/constants/translation/common.translation";
import { freelancerUser as freelancerUserRoutes } from "@/libs/routes";
import {
  useGetFreelancerUser,
  useApproveApplication,
  useRejectApplication,
  useApproveDocuments,
  useRejectDocuments,
  useApproveService,
  useRejectService,
} from "@/hooks/useFreelancerUser";
import { setFormatDate } from "@/utils/dateFormate";
import { TNTable } from "@/components";
import avatar from "@/assets/images/avatars/DefaultImg.png";
import constants from "@/libs/constant";

// Boolean fields round-trip through this app's response encryption as the strings
// "true"/"false" rather than real booleans (same as isOnlineValue in
// src/pages/user/dummy/[dummy_user_id]/view/index.js), so a plain truthy check on the
// value would treat the string "false" as true. Compare against both representations.
const isTrue = (value) => value === true || value === constants.status.apiStatus.true;

const TABS = {
  PERSONAL: "personal",
  CONTACT: "contact",
  PROFESSION: "profession",
  SERVICES: "services",
  AVAILABILITY: "availability",
  DOCUMENTS: "documents",
};

const getApplicationStatusBadgeClass = (status) => {
  switch (status) {
    case "approved":
    case "verified":
      return "success";
    case "rejected":
      return "danger";
    default:
      return "warning";
  }
};

const getVerificationStatusBadgeClass = (status) => {
  switch (status) {
    case "approved":
      return "success";
    case "rejected":
      return "danger";
    case "pending":
      return "warning";
    default:
      return "secondary";
  }
};

const ViewFreelancerUser = () => {
  const t = useTranslationContext();
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();

  const requestedTab = searchParams.get("tab");
  const [activeTab, setActiveTab] = useState(
    Object.values(TABS).includes(requestedTab) ? requestedTab : TABS.PERSONAL
  );

  const {
    data: response,
    isLoading,
    error: freelancerUserError,
    refetch,
  } = useGetFreelancerUser(id, null, (error) => {
    console.error("Error fetching freelancer user details:", error);
  });

  const freelancerUserData = response?.data;

  const { mutate: doApproveApplication } = useApproveApplication(() => {
    toast.success(t(freelancerUser.view.messages.approve_application_success));
    refetch();
  });
  const { mutate: doRejectApplication } = useRejectApplication(() => {
    toast.success(t(freelancerUser.view.messages.reject_application_success));
    refetch();
  });
  const { mutate: doApproveDocuments } = useApproveDocuments(() => {
    toast.success(t(freelancerUser.view.messages.approve_documents_success));
    refetch();
  });
  const { mutate: doRejectDocuments } = useRejectDocuments(() => {
    toast.success(t(freelancerUser.view.messages.reject_documents_success));
    refetch();
  });
  const { mutate: doApproveService } = useApproveService(() => {
    toast.success(t(freelancerUser.view.services.messages.approve_success));
    refetch();
  });
  const { mutate: doRejectService } = useRejectService(() => {
    toast.success(t(freelancerUser.view.services.messages.reject_success));
    refetch();
  });

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <CSpinner color="primary" />
      </div>
    );
  }

  if (freelancerUserError || !freelancerUserData) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "400px" }}>
        <div className="text-center">
          <h4>{t(freelancerUser.view.error.not_found)}</h4>
          <p>{t(freelancerUser.view.error.not_found_description)}</p>
          <CButton color="primary" onClick={() => navigate(freelancerUserRoutes.list)}>
            {t(freelancerUser.view.back_to_list)}
          </CButton>
        </div>
      </div>
    );
  }

  const na = t(freelancerUser.view.defaults.na);
  const isDeleted = !!freelancerUserData.deletedAt;

  const handleApproveApplication = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(freelancerUser.view.approve_application_popup.heading)}</h2>
            <p className="text-center mb-3">{t(freelancerUser.view.approve_application_popup.message)}</p>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doApproveApplication(freelancerUserData.uuid);
                  onClose();
                }}
              >
                {t(freelancerUser.view.approve_application_popup.yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(freelancerUser.view.approve_application_popup.no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const handleApproveDocuments = () => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(freelancerUser.view.approve_documents_popup.heading)}</h2>
            <p className="text-center mb-3">{t(freelancerUser.view.approve_documents_popup.message)}</p>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doApproveDocuments(freelancerUserData.uuid);
                  onClose();
                }}
              >
                {t(freelancerUser.view.approve_documents_popup.yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(freelancerUser.view.approve_documents_popup.no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const handleRejectWithReason = (popupKey, onConfirm) => {
    const popup = freelancerUser.view[popupKey];

    confirmAlert({
      customUI: ({ onClose }) => (
        <Formik
          initialValues={{ reason: "" }}
          validate={(values) => {
            const errors = {};
            if (!values.reason || !values.reason.trim()) {
              errors.reason = t(popup.reason_required);
            }
            return errors;
          }}
          onSubmit={(values, { setSubmitting }) => {
            onConfirm(freelancerUserData.uuid, values.reason.trim());
            setSubmitting(false);
            onClose();
          }}
        >
          {({ values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting }) => (
            <div className="alert-box admin-status-popup">
              <div className="alert-popup">
                <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
                <h2 className="alert-title">{t(popup.heading)}</h2>
                <p className="text-center mb-3">{t(popup.message)}</p>
                <div className="mb-3 text-start">
                  <CFormTextarea
                    id="reason"
                    name="reason"
                    rows={3}
                    placeholder={t(popup.reason_placeholder)}
                    value={values.reason}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    invalid={touched.reason && !!errors.reason}
                  />
                  {touched.reason && errors.reason && <div className="text-danger small mt-1">{errors.reason}</div>}
                </div>
                <div className="d-flex gap-2 justify-content-center flex-nowrap">
                  <CButton
                    color="primary"
                    className="table-delete-button"
                    disabled={isSubmitting}
                    onClick={handleSubmit}
                  >
                    {t(popup.yes_btn_text)}
                  </CButton>
                  <CButton className="table-primary-button" color="danger" onClick={onClose} disabled={isSubmitting}>
                    {t(popup.no_btn_text)}
                  </CButton>
                </div>
              </div>
            </div>
          )}
        </Formik>
      ),
    });
  };

  const handleRejectApplication = () =>
    handleRejectWithReason("reject_application_popup", (uuid, reason) => doRejectApplication({ uuid, reason }));

  const handleRejectDocuments = () =>
    handleRejectWithReason("reject_documents_popup", (uuid, reason) => doRejectDocuments({ uuid, reason }));

  const handleApproveService = (serviceUuid) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(freelancerUser.view.services.approve_popup.heading)}</h2>
            <p className="text-center mb-3">{t(freelancerUser.view.services.approve_popup.message)}</p>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doApproveService(serviceUuid);
                  onClose();
                }}
              >
                {t(freelancerUser.view.services.approve_popup.yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(freelancerUser.view.services.approve_popup.no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const handleRejectService = (serviceUuid) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-box">
          <div className="alert-popup">
            <FontAwesomeIcon className="alert-close" icon={faClose} onClick={onClose} />
            <h2 className="alert-title">{t(freelancerUser.view.services.reject_popup.heading)}</h2>
            <p className="text-center mb-3">{t(freelancerUser.view.services.reject_popup.message)}</p>
            <div className="d-flex gap-2 justify-content-center flex-nowrap">
              <CButton
                color="primary"
                className="table-delete-button"
                onClick={() => {
                  doRejectService(serviceUuid);
                  onClose();
                }}
              >
                {t(freelancerUser.view.services.reject_popup.yes_btn_text)}
              </CButton>
              <CButton className="table-primary-button" color="danger" onClick={onClose}>
                {t(freelancerUser.view.services.reject_popup.no_btn_text)}
              </CButton>
            </div>
          </div>
        </div>
      ),
    });
  };

  const handleViewService = (serviceUuid) => {
    navigate(freelancerUserRoutes.serviceView.replace(":id", id).replace(":serviceId", serviceUuid));
  };

  const services = freelancerUserData.services || [];
  const servicesPaginationData = {
    total: services.length,
    count: services.length,
    current_page: 1,
    per_page: services.length || 1,
    hasMorePages: false,
  };

  const serviceColumns = [
    {
      id: "service_name",
      accessorKey: "serviceName",
      header: t(freelancerUser.view.services.table.name_col_heading),
      enableSorting: true,
    },
    {
      id: "price",
      accessorKey: "price",
      header: t(freelancerUser.view.services.table.price_col_heading),
    },
    {
      id: "duration",
      accessorKey: "durationValue",
      header: t(freelancerUser.view.services.table.duration_col_heading),
      cell: ({ row }) => (
        <span>
          {row.original.durationValue} {row.original.durationUnit}
        </span>
      ),
    },
    {
      id: "status",
      accessorKey: "status",
      header: t(freelancerUser.view.services.table.status_col_heading),
      cell: ({ getValue }) => (
        <CBadge color={getApplicationStatusBadgeClass(getValue())} className="text-capitalize">
          {getValue()}
        </CBadge>
      ),
    },
    {
      id: "action",
      accessorKey: "uuid",
      header: t(freelancerUser.view.services.table.action_col_heading),
      cell: ({ row }) => {
        const service = row.original;
        return (
          <div className="d-flex justify-content-center gap-2">
            <OverlayTrigger
              placement="top"
              overlay={
                <Tooltip id={`view-service-${service.uuid}`}>{t(freelancerUser.view.services.view_tooltip)}</Tooltip>
              }
            >
              <CButton
                size="sm"
                style={{ backgroundColor: "#212631", borderColor: "#212631", color: "white" }}
                onClick={() => handleViewService(service.uuid)}
              >
                <FontAwesomeIcon icon={faEye} />
              </CButton>
            </OverlayTrigger>
            {!isDeleted && service.status !== "approved" && (
              <CButton size="sm" color="success" onClick={() => handleApproveService(service.uuid)}>
                <FontAwesomeIcon icon={faCheck} className="me-1" />
                {t(freelancerUser.view.services.actions.approve)}
              </CButton>
            )}
            {!isDeleted && service.status !== "rejected" && (
              <CButton size="sm" color="danger" onClick={() => handleRejectService(service.uuid)}>
                <FontAwesomeIcon icon={faXmark} className="me-1" />
                {t(freelancerUser.view.services.actions.reject)}
              </CButton>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="view-freelancer-user-container">
      <CCard className="mb-4 profile-header-card">
        <CCardBody>
          <CRow className="align-items-center">
            <CCol md="auto" className="text-center mb-3 mb-md-0">
              <CImage
                src={freelancerUserData.profileImageUrl || avatar}
                alt="profile_img"
                className="rounded-circle"
                style={{ width: "120px", height: "120px", objectFit: "cover", border: "4px solid #212631" }}
              />
            </CCol>
            <CCol>
              <h2 className="mb-1 d-flex align-items-center gap-2">
                {freelancerUserData.name || na}
                {isTrue(freelancerUserData.isDocumentVerified) && (
                  <CBadge
                    color="info"
                    style={{ fontSize: "0.75rem", padding: "0.35em 0.55em" }}
                  >
                    {t(freelancerUser.view.verified_badge)}
                  </CBadge>
                )}
                {isDeleted && (
                  <CBadge
                    color="danger"
                    style={{ fontSize: "0.75rem", padding: "0.35em 0.55em" }}
                  >
                    {t(freelancerUser.view.deleted_badge)}
                  </CBadge>
                )}
              </h2>
              <div className="text-muted mb-2">{freelancerUserData.user?.fullName || na}</div>
              <div className="d-flex flex-wrap gap-2 mb-2">
                <CBadge color={getApplicationStatusBadgeClass(freelancerUserData.applicationStatus)}>
                  {t(freelancerUser.list.application_status_filter[freelancerUserData.applicationStatus]) ||
                    freelancerUserData.applicationStatus}
                </CBadge>
                <CBadge color={getVerificationStatusBadgeClass(freelancerUserData.verificationStatus)}>
                  {t(freelancerUser.list.verification_status_filter[freelancerUserData.verificationStatus]) ||
                    freelancerUserData.verificationStatus}
                </CBadge>
              </div>
              {freelancerUserData.applicationStatus === "rejected" && freelancerUserData.applicationRejectionReason && (
                <div className="text-danger small mb-1">
                  <strong>{t(freelancerUser.view.application_rejection_reason_label)}:</strong>{" "}
                  {freelancerUserData.applicationRejectionReason}
                </div>
              )}
              {freelancerUserData.verificationStatus === "rejected" && freelancerUserData.rejectionReason && (
                <div className="text-danger small mb-2">
                  <strong>{t(freelancerUser.view.document_rejection_reason_label)}:</strong>{" "}
                  {freelancerUserData.rejectionReason}
                </div>
              )}
              {!isDeleted && (
                <div className="d-flex flex-wrap gap-2 mt-2">
                  {!["approved", "verified"].includes(freelancerUserData.applicationStatus) && (
                    <CButton size="sm" color="success" onClick={handleApproveApplication}>
                      {t(freelancerUser.view.actions.approve_application)}
                    </CButton>
                  )}
                  {freelancerUserData.applicationStatus !== "rejected" && (
                    <CButton size="sm" color="danger" onClick={handleRejectApplication}>
                      {t(freelancerUser.view.actions.reject_application)}
                    </CButton>
                  )}
                  {freelancerUserData.documents?.length > 0 && freelancerUserData.verificationStatus !== "approved" && (
                    <CButton size="sm" color="success" onClick={handleApproveDocuments}>
                      {t(freelancerUser.view.actions.approve_documents)}
                    </CButton>
                  )}
                  {freelancerUserData.documents?.length > 0 && freelancerUserData.verificationStatus !== "rejected" && (
                    <CButton size="sm" color="danger" onClick={handleRejectDocuments}>
                      {t(freelancerUser.view.actions.reject_documents)}
                    </CButton>
                  )}
                </div>
              )}
            </CCol>
          </CRow>
        </CCardBody>
      </CCard>

      <CNav variant="tabs" className="mb-4 view-user-tabs">
        {[
          [TABS.PERSONAL, faUser, freelancerUser.view.tabs.personal_details],
          [TABS.CONTACT, faAddressCard, freelancerUser.view.tabs.contact_details],
          [TABS.PROFESSION, faBriefcase, freelancerUser.view.tabs.profession],
          [TABS.SERVICES, faListCheck, freelancerUser.view.tabs.services_offered],
          [TABS.AVAILABILITY, faCalendarDays, freelancerUser.view.tabs.availability],
          [TABS.DOCUMENTS, faFileLines, freelancerUser.view.tabs.verification_documents],
        ].map(([tabKey, icon, labelKey]) => (
          <CNavItem key={tabKey}>
            <CNavLink
              href="#"
              active={activeTab === tabKey}
              onClick={(e) => {
                e.preventDefault();
                setActiveTab(tabKey);
              }}
            >
              <FontAwesomeIcon icon={icon} className="me-2" />
              {t(labelKey)}
            </CNavLink>
          </CNavItem>
        ))}
      </CNav>

      <CTabContent>
        <CTabPane visible={activeTab === TABS.PERSONAL}>
          <CCard>
            <CCardBody>
              <h4 className="card-title mb-4">{t(freelancerUser.view.personal_details.title)}</h4>
              <CRow>
                <CCol md={6} className="mb-3">
                  <strong>{t(freelancerUser.view.personal_details.business_name_label)}:</strong>
                  <p className="text-muted">{freelancerUserData.name || na}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(freelancerUser.view.personal_details.user_name_label)}:</strong>
                  <p className="text-muted">{freelancerUserData.user?.fullName || na}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(freelancerUser.view.personal_details.account_status_label)}:</strong>
                  <p className="text-muted">{freelancerUserData.user?.adminStatus || na}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(freelancerUser.view.personal_details.created_at_label)}:</strong>
                  <p className="text-muted">
                    {freelancerUserData.createdAt ? setFormatDate(freelancerUserData.createdAt) : na}
                  </p>
                </CCol>
                <CCol md={12} className="mb-3">
                  <strong>{t(freelancerUser.view.personal_details.bio_label)}:</strong>
                  <p className="text-muted">{freelancerUserData.bio || na}</p>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CTabPane>

        <CTabPane visible={activeTab === TABS.CONTACT}>
          <CCard>
            <CCardBody>
              <h4 className="card-title mb-4">{t(freelancerUser.view.contact_details.title)}</h4>
              <CRow>
                <CCol md={6} className="mb-3">
                  <strong>{t(freelancerUser.view.contact_details.email_label)}:</strong>
                  <p className="text-muted">{freelancerUserData.email || na}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(freelancerUser.view.contact_details.mobile_number_label)}:</strong>
                  <p className="text-muted">{freelancerUserData.mobileNumber || na}</p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(freelancerUser.view.contact_details.website_link_label)}:</strong>
                  <p className="text-muted">
                    {freelancerUserData.websiteLink ? (
                      <a href={freelancerUserData.websiteLink} target="_blank" rel="noreferrer" className="profile-link-blue">
                        {freelancerUserData.websiteLink}
                      </a>
                    ) : (
                      na
                    )}
                  </p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(freelancerUser.view.contact_details.instagram_link_label)}:</strong>
                  <p className="text-muted">
                    {freelancerUserData.instagramLink ? (
                      <a href={freelancerUserData.instagramLink} target="_blank" rel="noreferrer" className="profile-link-blue">
                        {freelancerUserData.instagramLink}
                      </a>
                    ) : (
                      na
                    )}
                  </p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(freelancerUser.view.contact_details.facebook_link_label)}:</strong>
                  <p className="text-muted">
                    {freelancerUserData.facebookLink ? (
                      <a href={freelancerUserData.facebookLink} target="_blank" rel="noreferrer" className="profile-link-blue">
                        {freelancerUserData.facebookLink}
                      </a>
                    ) : (
                      na
                    )}
                  </p>
                </CCol>
                <CCol md={6} className="mb-3">
                  <strong>{t(freelancerUser.view.contact_details.linkedin_link_label)}:</strong>
                  <p className="text-muted">
                    {freelancerUserData.linkedinLink ? (
                      <a href={freelancerUserData.linkedinLink} target="_blank" rel="noreferrer" className="profile-link-blue">
                        {freelancerUserData.linkedinLink}
                      </a>
                    ) : (
                      na
                    )}
                  </p>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CTabPane>

        <CTabPane visible={activeTab === TABS.PROFESSION}>
          <CCard>
            <CCardBody>
              <h4 className="card-title mb-4">{t(freelancerUser.view.profession_tab.title)}</h4>
              <CRow>
                <CCol md={6} className="mb-3">
                  <strong>{t(freelancerUser.view.profession_tab.primary_profession_label)}:</strong>
                  <p className="text-muted">{freelancerUserData.profession?.name || na}</p>
                </CCol>
                <CCol md={12} className="mb-3">
                  <strong>{t(freelancerUser.view.profession_tab.all_professions_label)}:</strong>
                  {freelancerUserData.professions?.length ? (
                    <div className="d-flex flex-wrap gap-2 mt-1">
                      {freelancerUserData.professions.map((profession) => (
                        <CBadge key={profession.uuid} color="info" className="p-2">
                          {profession.name}
                        </CBadge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-muted">{t(freelancerUser.view.profession_tab.no_professions)}</p>
                  )}
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CTabPane>

        <CTabPane visible={activeTab === TABS.SERVICES}>
          <CCard>
            <CCardBody>
              <h4 className="card-title mb-4">{t(freelancerUser.view.services.title)}</h4>
              <TNTable
                columns={serviceColumns}
                data={services}
                paginationData={servicesPaginationData}
                onSelectPage={() => {}}
                t={t}
              />
            </CCardBody>
          </CCard>
        </CTabPane>

        <CTabPane visible={activeTab === TABS.AVAILABILITY}>
          <CCard>
            <CCardBody>
              <h4 className="card-title mb-4">{t(freelancerUser.view.availability.title)}</h4>
              {freelancerUserData.availability?.length ? (
                <CRow>
                  {freelancerUserData.availability.map((slot) => (
                    <CCol md={6} key={slot.uuid} className="mb-2">
                      <div className="d-flex justify-content-between align-items-center border rounded p-2">
                        <span className="text-capitalize">{slot.dayOfWeek}</span>
                        {isTrue(slot.isOpen) ? (
                          <span>
                            {slot.startTime} - {slot.endTime}{" "}
                            <CBadge color="success">{t(freelancerUser.view.availability.open_label)}</CBadge>
                          </span>
                        ) : (
                          <CBadge color="secondary">{t(freelancerUser.view.availability.closed_label)}</CBadge>
                        )}
                      </div>
                    </CCol>
                  ))}
                </CRow>
              ) : (
                <p className="text-muted">{t(freelancerUser.view.availability.no_availability)}</p>
              )}
            </CCardBody>
          </CCard>
        </CTabPane>

        <CTabPane visible={activeTab === TABS.DOCUMENTS}>
          <CCard>
            <CCardBody>
              <h4 className="card-title mb-4">{t(freelancerUser.view.documents.title)}</h4>
              {(() => {
                const publicDocuments = (freelancerUserData.documents || []).filter((doc) => doc.type === "public");
                const privateDocuments = (freelancerUserData.documents || []).filter((doc) => doc.type !== "public");
                const renderDocuments = (docs) => (
                  <div className="d-flex flex-wrap gap-3">
                    {docs.map((doc) => (
                      <a
                        key={doc.uuid}
                        href={doc.docUrl}
                        download={doc.docName || true}
                        target="_blank"
                        rel="noreferrer"
                        className="text-center text-decoration-none"
                        style={{ width: "150px" }}
                      >
                        <div
                          className="d-flex align-items-center justify-content-center border rounded"
                          style={{ width: "150px", height: "150px" }}
                        >
                          <FontAwesomeIcon icon={faDownload} size="2x" />
                        </div>
                        <div className="small text-muted mt-1">{doc.docName}</div>
                      </a>
                    ))}
                  </div>
                );

                return (
                  <>
                    <h6 className="mb-3">{t(freelancerUser.view.documents.public_section_title)}</h6>
                    {publicDocuments.length ? (
                      renderDocuments(publicDocuments)
                    ) : (
                      <p className="text-muted">{t(freelancerUser.view.documents.no_public_documents)}</p>
                    )}

                    <hr className="my-4" />

                    <h6 className="mb-3">{t(freelancerUser.view.documents.private_section_title)}</h6>
                    {privateDocuments.length ? (
                      renderDocuments(privateDocuments)
                    ) : (
                      <p className="text-muted">{t(freelancerUser.view.documents.no_private_documents)}</p>
                    )}
                  </>
                );
              })()}
            </CCardBody>
          </CCard>
        </CTabPane>
      </CTabContent>
    </div>
  );
};

ViewFreelancerUser.breadCrumbArray = [
  {
    name: breadcrumb_key.freelancerUser?.list,
    pathname: freelancerUserRoutes.list,
    active: false,
  },
  {
    name: breadcrumb_key.freelancerUser?.view,
    pathname: "",
    active: true,
  },
];

export default ViewFreelancerUser;
